import { useState, useEffect, useCallback, useRef } from 'react';
import { FormCacheService, FormCacheData, FormStatus, ProgressInfo } from '../services/FormCacheService';

interface UseFormCacheOptions {
  userId: string;
  formType?: string;
  autoSaveDelay?: number;
  enableFirebase?: boolean;
}

interface UseFormCacheReturn {
  // Cache data
  cacheData: FormCacheData | null;
  isLoading: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  error: string | null;
  
  // Cache operations
  saveCache: (data: Partial<FormCacheData>) => Promise<void>;
  loadCache: () => Promise<void>;
  clearCache: () => Promise<void>;
  autoSave: (data: Partial<FormCacheData>) => void;
  
  // Status and progress
  getFormStatus: (formType: string) => FormStatus;
  getTotalProgress: () => ProgressInfo;
  
  // Utility
  hasUnsavedChanges: boolean;
  storageInfo: { used: number; available: number; percentage: number };
}

export const useFormCache = (options: UseFormCacheOptions): UseFormCacheReturn => {
  const { userId, formType, autoSaveDelay = 2000, enableFirebase = true } = options;
  
  // State
  const [cacheData, setCacheData] = useState<FormCacheData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Refs for auto-save
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingDataRef = useRef<Partial<FormCacheData> | null>(null);
  
  // Generate cache key
  const cacheKey = FormCacheService.generateCacheKey(userId, formType);
  
  // Load cache on mount
  useEffect(() => {
    loadCache();
  }, [userId, formType]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);
  
  // Load cache from storage
  const loadCache = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Try localStorage first
      let data = FormCacheService.loadFromLocalStorage(cacheKey);
      
      // If not found locally and Firebase is enabled, try Firestore
      if (!data && enableFirebase) {
        data = await FormCacheService.loadDraftFromFirestore(userId);
        
        // If found in Firestore, save to localStorage for faster access
        if (data) {
          FormCacheService.saveToLocalStorage(cacheKey, data);
        }
      }
      
      setCacheData(data);
      setLastSaved(data?.metadata.lastUpdated || null);
      setHasUnsavedChanges(false);
      
      console.log('📦 Cache loaded successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load cache';
      setError(errorMessage);
      console.error('Failed to load cache:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userId, formType, cacheKey, enableFirebase]);
  
  // Save cache to storage
  const saveCache = useCallback(async (data: Partial<FormCacheData>) => {
    setIsSaving(true);
    setError(null);
    
    try {
      // Merge with existing data
      const mergedData = cacheData 
        ? FormCacheService.mergeCacheData(cacheData, data)
        : createInitialCacheData(userId, data);
      
      // Validate data
      if (!FormCacheService.validateCacheData(mergedData)) {
        throw new Error('Invalid cache data');
      }
      
      // Save to localStorage
      FormCacheService.saveToLocalStorage(cacheKey, mergedData);
      
      // Save to Firestore if enabled
      if (enableFirebase) {
        await FormCacheService.saveDraftToFirestore(userId, mergedData);
      }
      
      setCacheData(mergedData);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      
      console.log('💾 Cache saved successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save cache';
      setError(errorMessage);
      console.error('Failed to save cache:', err);
    } finally {
      setIsSaving(false);
    }
  }, [cacheData, userId, cacheKey, enableFirebase]);
  
  // Clear cache
  const clearCache = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    
    try {
      // Clear from localStorage
      FormCacheService.clearLocalStorage(cacheKey);
      
      // Clear from Firestore if enabled
      if (enableFirebase) {
        await FormCacheService.deleteDraftFromFirestore(userId);
      }
      
      setCacheData(null);
      setLastSaved(null);
      setHasUnsavedChanges(false);
      
      console.log('🗑️ Cache cleared successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to clear cache';
      setError(errorMessage);
      console.error('Failed to clear cache:', err);
    } finally {
      setIsSaving(false);
    }
  }, [userId, cacheKey, enableFirebase]);
  
  // Auto-save with debouncing
  const autoSave = useCallback((data: Partial<FormCacheData>) => {
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Store pending data
    pendingDataRef.current = data;
    setHasUnsavedChanges(true);
    
    // Set new timeout
    autoSaveTimeoutRef.current = setTimeout(async () => {
      if (pendingDataRef.current) {
        await saveCache(pendingDataRef.current);
        pendingDataRef.current = null;
      }
    }, autoSaveDelay);
  }, [saveCache, autoSaveDelay]);
  
  // Get form status
  const getFormStatus = useCallback((formType: string): FormStatus => {
    if (!cacheData) {
      return { status: 'empty', itemCount: 0, totalAmount: 0 };
    }
    
    const formTypeKey = formType as keyof FormCacheData['formSelections'];
    const selections = cacheData.formSelections[formTypeKey] || {};
    
    return FormCacheService.calculateFormStatus(formTypeKey, selections);
  }, [cacheData]);
  
  // Get total progress
  const getTotalProgress = useCallback((): ProgressInfo => {
    if (!cacheData) {
      return {
        totalItems: 0,
        totalAmount: 0,
        completedForms: 0,
        totalForms: 4,
        percentage: 0,
      };
    }
    
    return FormCacheService.calculateProgress(cacheData);
  }, [cacheData]);
  
  // Get storage info
  const storageInfo = FormCacheService.getStorageInfo();
  
  return {
    // Cache data
    cacheData,
    isLoading,
    isSaving,
    lastSaved,
    error,
    
    // Cache operations
    saveCache,
    loadCache,
    clearCache,
    autoSave,
    
    // Status and progress
    getFormStatus,
    getTotalProgress,
    
    // Utility
    hasUnsavedChanges,
    storageInfo,
  };
};

// Helper function to create initial cache data
function createInitialCacheData(userId: string, data: Partial<FormCacheData>): FormCacheData {
  const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  return {
    orderData: {
      customerNumber: '',
      storeName: '',
      address: '',
      city: '',
      province: '',
      postalCode: '',
      email: '',
      phoneNumber: '',
      poNumber: '',
      orderDate: new Date().toISOString().split('T')[0],
      deliveryDate: '',
      orderType: null,
      specialInstructions: '',
      ...data.orderData,
    },
    formSelections: {
      heritage: {},
      livingston: {},
      flower: {},
      vegetable: {},
      ...data.formSelections,
    },
    uiState: {
      searchTerms: {},
      selectedCategories: {},
      collapsedSections: {},
      ...data.uiState,
    },
    metadata: {
      lastUpdated: new Date(),
      userId,
      sessionId,
      formStatus: 'draft',
      totalItems: 0,
      totalAmount: 0,
      version: 1,
      ...data.metadata,
    },
  };
}
